import { FC, useCallback, useState } from 'react'
import { Button, Box, Typography } from '@mui/material'
import { FFModal } from '~/shared/ui'
import { GroupedAppointmentTypes, VisitMix } from './utils'
import VisitMixEditor from './VisitMixEditor'
import { haveChanged } from '~/shared/lib/haveChanged'

interface Props {
  visitMixGroups: GroupedAppointmentTypes[]
  visitMix: Record<number, Record<string, VisitMix>> | null
  dayOfWeek: number
  fullDayName: string
  handleCancel: () => void
  handleSubmit: (visitMix: Record<number, Record<string, VisitMix>>) => void
  mappedAppointmentTypeKeys?: Set<string>
}

const EditVisitMixModal: FC<Props> = (props: Props) => {
  const [visitMixData, setVisitMixData] = useState<Record<number, Record<string, VisitMix>>>({})
  const [isEdited, setIsEdited] = useState<boolean>(false)
  const [error, setError] = useState<Record<number, boolean>>({})
  const [displayError, setDisplayError] = useState<boolean>(false)
  const [totalError, setTotalError] = useState<boolean>(false)

  // The editable mix resets to the loaded one whenever it or the groups change.
  const loadSource = [props.visitMix, props.visitMixGroups]
  const [loadedFrom, setLoadedFrom] = useState<unknown[]>()
  if (haveChanged(loadedFrom, loadSource)) {
    setLoadedFrom(loadSource)
    if (props.visitMix) {
      setVisitMixData(props.visitMix)
      // Initialize error state for each group
      const initialErrors: Record<number, boolean> = {}
      props.visitMixGroups.forEach(({ group }) => {
        if (group) {
          initialErrors[group.id] = false
        }
      })
      setError(initialErrors)
    }
  }

  const mappedKeys = props.mappedAppointmentTypeKeys

  // Check if any appointment type in a group is mapped
  const isGroupMapped = (groupData: GroupedAppointmentTypes): boolean => {
    if (!mappedKeys) return true
    return groupData.appointmentTypes.some(apptType => mappedKeys.has(apptType.unique_key))
  }

  // Get the list of mapped appointment type names for a group
  const getMappedAppointmentTypesInGroup = (groupData: GroupedAppointmentTypes): string[] => {
    if (!mappedKeys) return groupData.appointmentTypes.map(a => a.name)
    return groupData.appointmentTypes
      .filter(apptType => mappedKeys.has(apptType.unique_key))
      .map(a => a.name)
  }

  const handleUpdateVisitMix = (percentage: number, groupId: number) => {
    setVisitMixData(prevVisitMixData => {
      const dayOfWeek = props.dayOfWeek
      const existingDayMix = prevVisitMixData[dayOfWeek] ?? {}
      // Use group ID as the key (prefixed to distinguish from appointment type keys)
      const groupKey = `group_${groupId}`
      const existingEntry = existingDayMix[groupKey] ?? {
        id: null,
        percentage: 0,
        isEdited: false,
      }

      const updatedDayMix: Record<string, VisitMix> = {
        ...existingDayMix,
        [groupKey]: {
          ...existingEntry,
          percentage,
          isEdited: true,
        },
      }

      const nextVisitMixData = {
        ...prevVisitMixData,
        [dayOfWeek]: updatedDayMix,
      }

      // Calculate total from all group entries
      const totalPercentage = props.visitMixGroups.reduce((acc: number, { group }) => {
        if (!group) return acc
        const key = `group_${group.id}`
        return acc + (updatedDayMix[key]?.percentage ?? 0)
      }, 0)

      if (totalPercentage !== 100) setTotalError(true)
      else setTotalError(false)
      setIsEdited(true)
      return nextVisitMixData
    })
  }

  const handleUpdate = () => {
    const hasError = Object.values(error).some(e => e)
    if (hasError || totalError) {
      setDisplayError(true)
    } else {
      props.handleSubmit(visitMixData)
      setDisplayError(false)
      setIsEdited(false)
    }
  }

  const handleCancel = () => {
    setDisplayError(false)
    setIsEdited(false)
    props.handleCancel()
  }

  // Memoized callback to prevent infinite render loops in VisitMixEditor's useEffect
  const handleGroupError = useCallback((groupId: number, hasError: boolean) => {
    setError(prevError => ({ ...prevError, [groupId]: hasError }))
  }, [])

  if (!props.visitMix) return null

  return (
    <FFModal
      open={true}
      header={
        <Box sx={{ p: 1 }}>
          <Typography variant="h6" align="left">
            Edit Visit Mix
          </Typography>
        </Box>
      }
      footer={
        <Box sx={{ my: 1, mx: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button color="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Box sx={{ ml: 1 }}>
            <Button
              color="primary"
              type="submit"
              variant="contained"
              onClick={handleUpdate}
              disabled={!isEdited}
            >
              Update
            </Button>
          </Box>
        </Box>
      }
    >
      <Box sx={{ m: 2 }}>
        <Typography align="left" sx={{ fontSize: '1.6rem', fontWeight: 500 }}>
          {props.fullDayName}
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap' }}>
          {props.visitMixGroups.map(groupData => {
            const { group } = groupData
            if (!group) return null

            const groupKey = `group_${group.id}`
            const isMapped = isGroupMapped(groupData)
            const mappedApptTypes = getMappedAppointmentTypesInGroup(groupData)

            return (
              <Box key={group.id} sx={{ ml: 2, width: '25%', minWidth: '150px', mb: 2 }}>
                <VisitMixEditor
                  totalError={totalError}
                  groupTitle={group.title}
                  mappedAppointmentTypes={mappedApptTypes}
                  visitMixPercentage={visitMixData[props.dayOfWeek]?.[groupKey]?.percentage ?? null}
                  displayError={displayError}
                  disabled={!isMapped}
                  disabledTooltip="Provider has no appointment types enabled in this group"
                  onChange={visitMixPercentage =>
                    handleUpdateVisitMix(visitMixPercentage, group.id)
                  }
                  onError={hasError => handleGroupError(group.id, hasError)}
                />
              </Box>
            )
          })}
        </Box>
      </Box>
    </FFModal>
  )
}

export default EditVisitMixModal
